import React, { useState, useEffect, useCallback } from 'react';
import BucksWorkerOnlineOrderDetailsModal from './BucksWorkerOnlineOrderDetailsModal';
import '../stylings/BucksWorkerOnlineOrder.css';

// FIXED: Move API endpoint outside component to prevent recreation
const API_ENDPOINT = 'https://rv0kwp3fuc.execute-api.us-east-1.amazonaws.com/default/bucks-admin-online-orders-get';

// FIXED: Custom hook for debounced value to prevent excessive API calls
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

function BucksWorkerOnlineOrder() {
  // State management
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total_count: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [refreshing, setRefreshing] = useState(false);

  // FIXED: Use debounced search term to reduce API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Enhanced stats calculation
  const orderStats = React.useMemo(() => {
    if (!orders || orders.length === 0) {
      return {
        total: pagination.total_count || 0,
        processing: 0,
        prepared: 0,
        outForDelivery: 0,
        delivered: 0,
        todaysOrders: 0,
        todaysRevenue: 0
      };
    }

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Count orders by specific status
    let processing = 0;
    let prepared = 0;
    let outForDelivery = 0;
    let delivered = 0;
    let todaysOrders = 0;
    let todaysRevenue = 0;

    orders.forEach(order => {
      const status = order.status?.toLowerCase() || '';
      const originalStatus = order.original_status?.toLowerCase() || '';
      const orderDate = new Date(order.created_at);
      const orderTotal = parseFloat(order.total) || 0;

      // Specific status categorization
      if (status === 'processing') {
        processing++;
      } else if (originalStatus === 'prepared') {
        prepared++;
      } else if (originalStatus === 'out for delivery') {
        outForDelivery++;
      } else if (originalStatus === 'delivered' || originalStatus === 'picked up') {
        delivered++;
      }

      // Today's stats
      if (orderDate >= todayStart) {
        todaysOrders++;
        todaysRevenue += orderTotal;
      }
    });

    return {
      total: pagination.total_count || orders.length,
      processing,
      prepared,
      outForDelivery,
      delivered,
      todaysOrders,
      todaysRevenue
    };
  }, [orders, pagination.total_count]);

  // FIXED: Stable fetch function with proper dependencies
  const fetchOrders = useCallback(async (page = 1, search = '', status = 'all') => {
    try {
      setLoading(page === 1);
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });

      if (search.trim()) {
        params.append('search', search.trim());
      }

      if (status && status !== 'all') {
        params.append('status', status);
      }

      // FIXED: Reduce console logging in production
      if (process.env.NODE_ENV === 'development') {
        console.log('=== Fetching Orders ===');
        console.log('API Endpoint:', `${API_ENDPOINT}?${params}`);
        console.log('Search term:', search);
        console.log('Status filter:', status);
        console.log('Page:', page);
      }

      const response = await fetch(`${API_ENDPOINT}?${params}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (process.env.NODE_ENV === 'development') {
        console.log('Raw API response:', data);
      }
      
      if (data.orders) {
        // Ensure each order has both status and original_status fields properly set
        const processedOrders = data.orders.map(order => ({
          ...order,
          // Make sure we have consistent status fields
          status: order.status || order.order_status || 'Pending',
          original_status: order.original_status || order.order_status || order.status || 'Pending'
        }));
        
        if (process.env.NODE_ENV === 'development') {
          console.log('=== Order Processing ===');
          console.log('Original orders count:', data.orders.length);
          console.log('Processed orders:', processedOrders);
          console.log('Sample processed order status fields:');
          if (processedOrders.length > 0) {
            console.log('First order status mapping:', {
              original_status_field: data.orders[0].original_status,
              order_status_field: data.orders[0].order_status,
              status_field: data.orders[0].status,
              final_status: processedOrders[0].status,
              final_original_status: processedOrders[0].original_status
            });
          }
          console.log('========================');
        }
        
        setOrders(processedOrders);
        setPagination(data.pagination);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('=== Fetch Orders Error ===');
      console.error('Failed to fetch orders:', err);
      console.error('Error details:', err.message);
      console.error('=========================');
      setError(err.message);
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []); // FIXED: Remove API_ENDPOINT dependency since it's now constant

  // FIXED: Initial load only
  useEffect(() => {
    fetchOrders(1, '', 'all');
  }, [fetchOrders]);

  // FIXED: Trigger fetch when debounced values change
  useEffect(() => {
    // Only fetch if we're not in the initial state or if filters have actually changed
    if (debouncedSearchTerm !== '' || statusFilter !== 'all') {
      fetchOrders(1, debouncedSearchTerm, statusFilter);
    }
  }, [debouncedSearchTerm, statusFilter, fetchOrders]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchOrders(pagination.page, debouncedSearchTerm, statusFilter);
  }, [fetchOrders, pagination.page, debouncedSearchTerm, statusFilter]);

  // Handle pagination
  const handlePageChange = useCallback((newPage) => {
    fetchOrders(newPage, debouncedSearchTerm, statusFilter);
  }, [fetchOrders, debouncedSearchTerm, statusFilter]);

  // Handle order click
  const handleOrderClick = useCallback((order) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Order clicked:', order);
      console.log('Order status fields:', {
        status: order.status,
        original_status: order.original_status,
        order_status: order.order_status
      });
    }
    setSelectedOrder(order);
    setShowModal(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setShowModal(false);
    setSelectedOrder(null);
  }, []);

  // Handle order update from modal
  const handleOrderUpdate = useCallback(() => {
    handleModalClose();
    handleRefresh();
  }, [handleModalClose, handleRefresh]);

  // Update getStatusBadgeClass function
  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bucks-status-pending';
      case 'processing':
        return 'bucks-status-processing';
      case 'prepared':
        return 'bucks-status-prepared';
      case 'shipped':
      case 'out for delivery':
      case 'ready for pickup':
        return 'bucks-status-shipped';
      case 'delivered':
      case 'picked up':
        return 'bucks-status-delivered';
      case 'cancelled':
        return 'bucks-status-cancelled';
      case 'refunded':
        return 'bucks-status-refunded';
      default:
        return 'bucks-status-pending';
    }
  };

  // Get status display text
  const getStatusText = (order) => {
    if (order.original_status) {
      return order.original_status;
    }
    return order.status || 'Pending';
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Get delivery method icon
  const getDeliveryIcon = (method) => {
    if (method?.toLowerCase() === 'delivery') {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M1 3h15v13H1z"/>
          <path d="M16 8h4l3 3v5h-7V8z"/>
          <circle cx="5.5" cy="18.5" r="2.5"/>
          <circle cx="18.5" cy="18.5" r="2.5"/>
        </svg>
      );
    } else {
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>
      );
    }
  };

  // Calculate priority score for sorting
  const getPriorityScore = (order) => {
    const status = order.status?.toLowerCase();
    const originalStatus = order.original_status?.toLowerCase();
    
    // Higher priority for ready orders
    if (originalStatus === 'ready for pickup' || originalStatus === 'out for delivery') return 6;
    if (originalStatus === 'prepared') return 5;
    if (status === 'processing') return 4;
    if (status === 'pending') return 3;
    if (status === 'shipped') return 2;
    return 1;
  };

  // Sort orders by priority and date
  const sortedOrders = React.useMemo(() => {
    return [...orders].sort((a, b) => {
      const priorityDiff = getPriorityScore(b) - getPriorityScore(a);
      if (priorityDiff !== 0) return priorityDiff;
      
      // If same priority, sort by date (newest first)
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
  }, [orders]);

  if (loading) {
    return (
      <div className="bucks-orders-main-container">
        <div className="bucks-loading-state">
          <div className="bucks-loading-spinner"></div>
          <div className="bucks-loading-content">
            <h3>Loading Your Orders</h3>
            <p>Please wait while we fetch your latest orders...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bucks-orders-main-container">
        <div className="bucks-error-state">
          <div className="bucks-error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <div className="bucks-error-content">
            <h3>Oops! Something went wrong</h3>
            <p>{error}</p>
            <button onClick={handleRefresh} className="bucks-retry-button">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bucks-orders-main-container">
      {/* Enhanced Header Section with Dynamic Stats */}
      <div className="bucks-orders-header">
        <div className="bucks-header-content">
          <div className="bucks-title-section">
            <h1 className="bucks-main-title">Online Orders</h1>
            <div className="bucks-orders-counter">
              <span className="bucks-counter-number">{orderStats.total}</span>
              <span className="bucks-counter-text">
                {orderStats.total === 1 ? 'Order' : 'Orders'} Total
              </span>
            </div>
          </div>
          
          {/* Enhanced Stats Grid */}
          <div className="bucks-stats-grid">
            {/* Processing */}
            <div className="bucks-stat-card bucks-stat-processing">
              <div className="bucks-stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
              </div>
              <div className="bucks-stat-content">
                <div className="bucks-stat-number">{orderStats.processing}</div>
                <div className="bucks-stat-label">Processing</div>
              </div>
            </div>

            {/* Prepared */}
            <div className="bucks-stat-card bucks-stat-prepared">
              <div className="bucks-stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22,4 12,14.01 9,11.01"/>
                </svg>
              </div>
              <div className="bucks-stat-content">
                <div className="bucks-stat-number">{orderStats.prepared}</div>
                <div className="bucks-stat-label">Prepared</div>
              </div>
            </div>

            {/* Out for Delivery */}
            <div className="bucks-stat-card bucks-stat-delivery">
              <div className="bucks-stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 3h15v13H1z"/>
                  <path d="M16 8h4l3 3v5h-7V8z"/>
                  <circle cx="5.5" cy="18.5" r="2.5"/>
                  <circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
              </div>
              <div className="bucks-stat-content">
                <div className="bucks-stat-number">{orderStats.outForDelivery}</div>
                <div className="bucks-stat-label">Out for Delivery</div>
              </div>
            </div>

            {/* Delivered */}
            <div className="bucks-stat-card bucks-stat-delivered">
              <div className="bucks-stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4"/>
                  <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.35 0 4.47.9 6.06 2.38"/>
                </svg>
              </div>
              <div className="bucks-stat-content">
                <div className="bucks-stat-number">{orderStats.delivered}</div>
                <div className="bucks-stat-label">Delivered</div>
              </div>
            </div>

            {/* Today's Orders */}
            <div className="bucks-stat-card bucks-stat-today">
              <div className="bucks-stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="bucks-stat-content">
                <div className="bucks-stat-number">{orderStats.todaysOrders}</div>
                <div className="bucks-stat-label">Today</div>
              </div>
            </div>

          </div>
          
          <div className="bucks-header-actions">
            <button 
              onClick={handleRefresh} 
              className={`bucks-refresh-btn ${refreshing ? 'bucks-refreshing' : ''}`}
              disabled={refreshing}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
              </svg>
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search and Filter Section */}
      <div className="bucks-filters-section">
        <div className="bucks-search-wrapper">
          <div className="bucks-search-container">
            <div className="bucks-search-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by order number, customer name, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bucks-search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="bucks-clear-search"
                aria-label="Clear search"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
        </div>
        
        <div className="bucks-filter-wrapper">
          <label htmlFor="status-filter" className="bucks-filter-label">Filter by Status:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bucks-status-select"
          >
            <option value="all">All Orders</option>
            <option value="Pending">Pending</option>
            <option value="Processing">Processing</option>
            <option value="Prepared">Prepared</option>
            <option value="Out for Delivery">Out for Delivery</option>
            <option value="Ready for Pickup">Ready for Pickup</option>
            <option value="Delivered">Delivered</option>
            <option value="Picked Up">Picked Up</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {/* Orders Content */}
      {sortedOrders.length === 0 ? (
        <div className="bucks-empty-state">
          <div className="bucks-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 11V7a4 4 0 0 0-8 0v4H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2h-3z"/>
            </svg>
          </div>
          <div className="bucks-empty-content">
            <h3>No Orders Found</h3>
            <p>
              {searchTerm || statusFilter !== 'all' 
                ? "Try adjusting your search or filter criteria to find more orders."
                : "No orders have been placed yet. Orders will appear here when customers place them."
              }
            </p>
            {(searchTerm || statusFilter !== 'all') && (
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="bucks-clear-filters-btn"
              >
                Clear All Filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bucks-orders-grid">
          {sortedOrders.map((order) => (
            <div
              key={order.internal_id || order.id}
              className="bucks-order-card"
              onClick={() => handleOrderClick(order)}
            >
              {/* Order Header */}
              <div className="bucks-order-header">
                <div className="bucks-order-number">
                  <span className="bucks-order-label">Order</span>
                  <span className="bucks-order-id">#{order.id}</span>
                </div>
                <div className={`bucks-status-badge ${getStatusBadgeClass(order.status)}`}>
                  {getStatusText(order)}
                </div>
              </div>

              {/* Order Info */}
              <div className="bucks-order-info">
                <div className="bucks-order-meta">
                  <div className="bucks-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12,6 12,12 16,14"/>
                    </svg>
                    <span>{formatDate(order.created_at)}</span>
                  </div>
                  <div className="bucks-meta-item">
                    {getDeliveryIcon(order.delivery_method)}
                    <span>{order.delivery_method || 'Pickup'}</span>
                  </div>
                </div>
              </div>

              {/* Customer Section */}
              <div className="bucks-customer-section">
                <div className="bucks-customer-avatar">
                  {order.customer?.name?.charAt(0)?.toUpperCase() || 'C'}
                </div>
                <div className="bucks-customer-details">
                  <div className="bucks-customer-name">
                    {order.customer?.name || 'Unknown Customer'}
                  </div>
                  <div className="bucks-customer-contact">
                    {order.customer?.phone || order.customer?.email || 'No contact info'}
                  </div>
                </div>
              </div>

              {/* Items Preview */}
              <div className="bucks-items-preview">
                <div className="bucks-items-header">
                  <span className="bucks-items-count">
                    {order.items?.length || 0} {(order.items?.length || 0) === 1 ? 'Item' : 'Items'}
                  </span>
                </div>
                <div className="bucks-items-list">
                  {order.items?.slice(0, 3).map((item, index) => (
                    <div key={item.id || index} className="bucks-item-preview">
                      <span className="bucks-item-quantity">{item.quantity}×</span>
                      <span className="bucks-item-name">{item.name}</span>
                    </div>
                  ))}
                  {(order.items?.length || 0) > 3 && (
                    <div className="bucks-more-items">
                      +{(order.items?.length || 0) - 3} more items
                    </div>
                  )}
                </div>
              </div>

              {/* Order Total */}
              <div className="bucks-order-total">
                <div className="bucks-total-label">Total</div>
                <div className="bucks-total-amount">{formatCurrency(order.total)}</div>
              </div>

              {/* Action Button */}
              <div className="bucks-order-actions">
                <button className="bucks-view-details-btn">
                  <span>View Details</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="m9 18 6-6-6-6"/>
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="bucks-pagination-container">
          <div className="bucks-pagination-content">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={!pagination.has_prev}
              className="bucks-pagination-btn bucks-prev-btn"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m15 18-6-6 6-6"/>
              </svg>
              <span>Previous</span>
            </button>
            
            <div className="bucks-pagination-info">
              <span className="bucks-page-current">Page {pagination.page}</span>
              <span className="bucks-page-divider">of</span>
              <span className="bucks-page-total">{pagination.total_pages}</span>
            </div>
            
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.has_next}
              className="bucks-pagination-btn bucks-next-btn"
            >
              <span>Next</span>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showModal && selectedOrder && (
        <BucksWorkerOnlineOrderDetailsModal
          order={selectedOrder}
          onClose={handleModalClose}
          onOrderUpdate={handleOrderUpdate}
        />
      )}
    </div>
  );
}

export default BucksWorkerOnlineOrder;